import { useMemo, useState } from 'react';
import {
  useGradeStatsOverview,
  useLessonGradesByCategory,
  useLessonGradesByClass,
  useLessonGradesByMonth,
  usePeriodGradesByCategory,
  usePeriodGradesByClass,
  usePeriodGradesByGradingPeriod,
} from '@/features/grade-stats/grade-stats.queries';
import { format, subMonths } from 'date-fns';
import { el } from 'date-fns/locale';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BookOpen,
  CalendarClock,
  GraduationCap,
  Layers,
  Users,
} from 'lucide-react';
import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts';

import {
  LessonGradesByCategoryItem,
  LessonGradesByClassItem,
  PeriodGradesByCategoryItem,
  PeriodGradesByClassItem,
  PeriodGradesByGradingPeriodItem,
} from '@/types/grade-stats.types';
import { cn } from '@/lib/utils';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// ── Helpers ───────────────────────────────────────────────────────────────────

const now = new Date();

const buildMonthOptions = () => {
  const options: { value: string; label: string }[] = [];
  for (let i = 11; i >= 0; i--) {
    const d = subMonths(now, i);
    options.push({
      value: format(d, 'yyyy-MM'),
      label: format(d, 'LLLL yyyy', { locale: el }),
    });
  }
  return options;
};

const MONTH_OPTIONS = buildMonthOptions();

const defaultFrom = format(subMonths(now, 2), 'yyyy-MM');
const defaultTo = format(now, 'yyyy-MM');

const gradeColor = (grade: number) => {
  if (grade >= 8) return 'text-emerald-600 dark:text-emerald-400';
  if (grade >= 5) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
};

const formatMonth = (m: string) => {
  const [year, month] = m.split('-');
  const d = new Date(parseInt(year), parseInt(month) - 1, 1);
  return format(d, 'LLL yy', { locale: el });
};

const formatDateRange = (start: string, end: string) => {
  const s = format(new Date(start), 'dd MMM yyyy', { locale: el });
  const e = format(new Date(end), 'dd MMM yyyy', { locale: el });
  return `${s} - ${e}`;
};

// ── Chart configs ─────────────────────────────────────────────────────────────

const lessonMonthChartConfig = {
  totalGrades: {
    label: 'Βαθμολογίες',
    color: 'hsl(var(--chart-1))',
  },
  averageGrade: {
    label: 'Μ.Ο.',
    color: 'hsl(var(--chart-2))',
  },
} satisfies ChartConfig;

// ── Sorting ───────────────────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc' | null;

function useSortableTable<T>(data: T[] | undefined) {
  const [sortKey, setSortKey] = useState<keyof T | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  const toggle = (key: keyof T) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'));
      if (sortDir === 'desc') setSortKey(null);
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!data || !sortKey || !sortDir) return data ?? [];
    return [...data].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv), 'el')
        : String(bv).localeCompare(String(av), 'el');
    });
  }, [data, sortKey, sortDir]);

  const SortIcon = ({ column }: { column: keyof T }) => {
    if (sortKey !== column || !sortDir) return <ArrowUpDown className='ml-1 inline h-3 w-3' />;
    return sortDir === 'asc' ? (
      <ArrowUp className='ml-1 inline h-3 w-3' />
    ) : (
      <ArrowDown className='ml-1 inline h-3 w-3' />
    );
  };

  return { sorted, toggle, SortIcon };
}

// ── Reusable UI pieces ────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        {[1, 2].map((i) => (
          <Skeleton key={i} className='h-32 rounded-lg' />
        ))}
      </div>
      <Skeleton className='h-64 rounded-lg' />
      <div className='grid grid-cols-2 gap-4'>
        <Skeleton className='h-64 rounded-lg' />
        <Skeleton className='h-64 rounded-lg' />
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  valueClass,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  valueClass?: string;
}) {
  return (
    <div className='flex items-start gap-3'>
      <div className='rounded-md bg-muted p-2'>
        <Icon className='h-4 w-4 text-muted-foreground' />
      </div>
      <div>
        <p className='text-xs font-medium text-muted-foreground'>{label}</p>
        <p className={cn('text-lg font-bold tabular-nums', valueClass)}>{value}</p>
      </div>
    </div>
  );
}

function GradesByClassTable({
  title,
  data,
  isLoading,
}: {
  title: string;
  data: (LessonGradesByClassItem | PeriodGradesByClassItem)[] | undefined;
  isLoading: boolean;
}) {
  const { sorted, toggle, SortIcon } = useSortableTable(data);

  if (isLoading) return <Skeleton className='h-64 rounded-lg' />;

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-sm font-medium'>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>Δεν υπάρχουν δεδομένα</p>
        ) : (
          <div className='max-h-[400px] overflow-auto rounded-md border'>
            <table className='w-full text-xs'>
              <thead className='sticky top-0 bg-background'>
                <tr className='border-b'>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-left font-medium text-muted-foreground'
                    onClick={() => toggle('className' as keyof (typeof sorted)[0])}
                  >
                    Τμήμα
                    <SortIcon column={'className' as keyof (typeof sorted)[0]} />
                  </th>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-left font-medium text-muted-foreground'
                    onClick={() => toggle('subjectName' as keyof (typeof sorted)[0])}
                  >
                    Μάθημα
                    <SortIcon column={'subjectName' as keyof (typeof sorted)[0]} />
                  </th>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-right font-medium text-muted-foreground'
                    onClick={() => toggle('totalGrades' as keyof (typeof sorted)[0])}
                  >
                    Σύνολο Βαθμών
                    <SortIcon column={'totalGrades' as keyof (typeof sorted)[0]} />
                  </th>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-right font-medium text-muted-foreground'
                    onClick={() => toggle('averageGrade' as keyof (typeof sorted)[0])}
                  >
                    Μ.Ο.
                    <SortIcon column={'averageGrade' as keyof (typeof sorted)[0]} />
                  </th>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-right font-medium text-muted-foreground'
                    onClick={() => toggle('studentsGraded' as keyof (typeof sorted)[0])}
                  >
                    Μαθητές
                    <SortIcon column={'studentsGraded' as keyof (typeof sorted)[0]} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row, i) => (
                  <tr
                    key={`${row.classId}-${row.subjectName}-${i}`}
                    className='border-b transition-colors last:border-0 hover:bg-muted/50'
                  >
                    <td className='px-3 py-2.5 font-medium'>{row.className}</td>
                    <td className='px-3 py-2.5'>{row.subjectName}</td>
                    <td className='px-3 py-2.5 text-right tabular-nums'>{row.totalGrades}</td>
                    <td
                      className={cn(
                        'px-3 py-2.5 text-right font-semibold tabular-nums',
                        gradeColor(row.averageGrade)
                      )}
                    >
                      {row.averageGrade.toFixed(1)}
                    </td>
                    <td className='px-3 py-2.5 text-right tabular-nums'>{row.studentsGraded}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CategoryAccordion({
  title,
  data,
  isLoading,
}: {
  title: string;
  data: (LessonGradesByCategoryItem | PeriodGradesByCategoryItem)[] | undefined;
  isLoading: boolean;
}) {
  const { sorted } = useSortableTable(data);

  if (isLoading) return <Skeleton className='h-64 rounded-lg' />;

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-sm font-medium'>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>Δεν υπάρχουν δεδομένα</p>
        ) : (
          <Accordion type='single' collapsible className='w-full space-y-2'>
            {sorted.map((row, i) => (
              <AccordionItem key={`${row.categoryId ?? 'uncat'}-${i}`} value={`cat-${i}`}>
                <AccordionTrigger className='w-full'>
                  <div className='grid w-full grid-cols-4 items-center gap-2 text-left text-xs font-medium text-muted-foreground'>
                    <span className='text-foreground'>{row.categoryName}</span>
                    <span className='text-right tabular-nums'>{row.totalGrades}</span>
                    <span
                      className={cn(
                        'text-right font-semibold tabular-nums',
                        gradeColor(row.averageGrade)
                      )}
                    >
                      {row.averageGrade.toFixed(1)}
                    </span>
                    <span className='text-right tabular-nums'>{row.studentsGraded}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {row.classes?.length ? (
                    <div className='mt-2 overflow-auto rounded-md border'>
                      <table className='w-full text-xs'>
                        <thead className='sticky top-0 bg-background'>
                          <tr className='border-b'>
                            <th className='px-3 py-2.5 text-left font-medium text-muted-foreground'>
                              Τμήμα
                            </th>
                            <th className='px-3 py-2.5 text-left font-medium text-muted-foreground'>
                              Μάθημα
                            </th>
                            <th className='px-3 py-2.5 text-right font-medium text-muted-foreground'>
                              Σύνολο
                            </th>
                            <th className='px-3 py-2.5 text-right font-medium text-muted-foreground'>
                              Μ.Ο.
                            </th>
                            <th className='px-3 py-2.5 text-right font-medium text-muted-foreground'>
                              Μαθητές
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.classes.map((c, idx) => (
                            <tr
                              key={`${c.classId}-${c.subjectName}-${idx}`}
                              className='border-b transition-colors last:border-0 hover:bg-muted/50'
                            >
                              <td className='px-3 py-2.5 font-medium'>{c.className}</td>
                              <td className='px-3 py-2.5'>{c.subjectName}</td>
                              <td className='px-3 py-2.5 text-right tabular-nums'>
                                {c.totalGrades}
                              </td>
                              <td
                                className={cn(
                                  'px-3 py-2.5 text-right font-semibold tabular-nums',
                                  gradeColor(c.averageGrade)
                                )}
                              >
                                {c.averageGrade.toFixed(1)}
                              </td>
                              <td className='px-3 py-2.5 text-right tabular-nums'>
                                {c.studentsGraded}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className='py-2 text-xs text-muted-foreground'>Δεν υπάρχουν τμήματα</p>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}

function GradingPeriodsTable({
  data,
  isLoading,
}: {
  data: PeriodGradesByGradingPeriodItem[] | undefined;
  isLoading: boolean;
}) {
  const { sorted, toggle, SortIcon } = useSortableTable(data);

  if (isLoading) return <Skeleton className='h-64 rounded-lg' />;

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-sm font-medium'>Βαθμοί ανά Περίοδο</CardTitle>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>Δεν υπάρχουν δεδομένα</p>
        ) : (
          <div className='max-h-[360px] overflow-auto rounded-md border'>
            <table className='w-full text-xs'>
              <thead className='sticky top-0 bg-background'>
                <tr className='border-b'>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-left font-medium text-muted-foreground'
                    onClick={() => toggle('gradingPeriodName')}
                  >
                    Περίοδος
                    <SortIcon column='gradingPeriodName' />
                  </th>
                  <th className='px-3 py-2.5 text-left font-medium text-muted-foreground'>
                    Ημερομηνίες
                  </th>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-right font-medium text-muted-foreground'
                    onClick={() => toggle('totalGrades')}
                  >
                    Σύνολο Βαθμών
                    <SortIcon column='totalGrades' />
                  </th>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-right font-medium text-muted-foreground'
                    onClick={() => toggle('averageGrade')}
                  >
                    Μ.Ο.
                    <SortIcon column='averageGrade' />
                  </th>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-right font-medium text-muted-foreground'
                    onClick={() => toggle('studentsGraded')}
                  >
                    Μαθητές
                    <SortIcon column='studentsGraded' />
                  </th>
                  <th
                    className='cursor-pointer px-3 py-2.5 text-right font-medium text-muted-foreground'
                    onClick={() => toggle('classesWithGrades')}
                  >
                    Τμήματα
                    <SortIcon column='classesWithGrades' />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => (
                  <tr
                    key={row.gradingPeriodId}
                    className='border-b transition-colors last:border-0 hover:bg-muted/50'
                  >
                    <td className='px-3 py-2.5 font-medium'>{row.gradingPeriodName}</td>
                    <td className='px-3 py-2.5 text-muted-foreground'>
                      {formatDateRange(row.startDate, row.endDate)}
                    </td>
                    <td className='px-3 py-2.5 text-right tabular-nums'>{row.totalGrades}</td>
                    <td
                      className={cn(
                        'px-3 py-2.5 text-right font-semibold tabular-nums',
                        gradeColor(row.averageGrade)
                      )}
                    >
                      {row.averageGrade.toFixed(1)}
                    </td>
                    <td className='px-3 py-2.5 text-right tabular-nums'>{row.studentsGraded}</td>
                    <td className='px-3 py-2.5 text-right tabular-nums'>{row.classesWithGrades}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

type GradesTabProps = {
  fromMonth?: string;
  toMonth?: string;
  hideRangePicker?: boolean;
};

function GradesTab({ fromMonth: propFrom, toMonth: propTo, hideRangePicker }: GradesTabProps) {
  const [fromMonth, setFromMonth] = useState(propFrom ?? defaultFrom);
  const [toMonth, setToMonth] = useState(propTo ?? defaultTo);

  const effectiveFrom = propFrom ?? fromMonth;
  const effectiveTo = propTo ?? toMonth;

  const params = { fromMonth: effectiveFrom, toMonth: effectiveTo };

  const { data: overview, isLoading: overviewLoading, error } = useGradeStatsOverview(params);
  const { data: lessonByClass, isLoading: lessonClassLoading } = useLessonGradesByClass(params);
  const { data: lessonByMonth, isLoading: lessonMonthLoading } = useLessonGradesByMonth(params);
  const { data: lessonByCategory, isLoading: lessonCategoryLoading } =
    useLessonGradesByCategory(params);
  const { data: periodByClass, isLoading: periodClassLoading } = usePeriodGradesByClass(params);
  const { data: periodByPeriod, isLoading: periodByPeriodLoading } =
    usePeriodGradesByGradingPeriod(params);
  const { data: periodByCategory, isLoading: periodCategoryLoading } =
    usePeriodGradesByCategory(params);

  return (
    <div className='space-y-6 p-6'>
      {!hideRangePicker && (
        <div className='flex items-center gap-3'>
          <Select value={effectiveFrom} onValueChange={setFromMonth}>
            <SelectTrigger className='w-[180px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Από</SelectLabel>
                {MONTH_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <span className='text-sm text-muted-foreground'>—</span>
          <Select value={effectiveTo} onValueChange={setToMonth}>
            <SelectTrigger className='w-[180px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Έως</SelectLabel>
                {MONTH_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      )}

      {error ? (
        <div className='flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/5 p-4'>
          <AlertCircle className='h-4 w-4 text-destructive' />
          <p className='text-sm text-destructive'>Σφάλμα φόρτωσης δεδομένων.</p>
        </div>
      ) : overviewLoading ? (
        <LoadingSkeleton />
      ) : overview ? (
        <>
          {/* ── Overview cards ──────────────────────────── */}
          <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
            <Card>
              <CardHeader className='pb-2'>
                <div className='flex items-center gap-2'>
                  <BookOpen className='h-4 w-4 text-muted-foreground' />
                  <CardTitle className='text-sm font-medium'>Βαθμοί Μαθήματος</CardTitle>
                </div>
              </CardHeader>
              <CardContent className='grid grid-cols-2 gap-4'>
                <StatCard
                  label='Σύνολο Βαθμών'
                  value={overview.lessonGrades.totalGrades.toLocaleString('el-GR')}
                  icon={GraduationCap}
                />
                <StatCard
                  label='Μέσος Όρος'
                  value={overview.lessonGrades.averageGrade.toFixed(1)}
                  icon={GraduationCap}
                  valueClass={gradeColor(overview.lessonGrades.averageGrade)}
                />
                <StatCard
                  label='Μαθητές'
                  value={overview.lessonGrades.studentsGraded.toLocaleString('el-GR')}
                  icon={Users}
                />
                <StatCard
                  label='Τμήματα'
                  value={overview.lessonGrades.classesWithGrades}
                  icon={Layers}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className='pb-2'>
                <div className='flex items-center gap-2'>
                  <CalendarClock className='h-4 w-4 text-muted-foreground' />
                  <CardTitle className='text-sm font-medium'>Βαθμοί Περιόδου</CardTitle>
                </div>
              </CardHeader>
              <CardContent className='grid grid-cols-2 gap-4'>
                <StatCard
                  label='Σύνολο Βαθμών'
                  value={overview.periodGrades.totalGrades.toLocaleString('el-GR')}
                  icon={GraduationCap}
                />
                <StatCard
                  label='Μέσος Όρος'
                  value={overview.periodGrades.averageGrade.toFixed(1)}
                  icon={GraduationCap}
                  valueClass={gradeColor(overview.periodGrades.averageGrade)}
                />
                <StatCard
                  label='Μαθητές'
                  value={overview.periodGrades.studentsGraded.toLocaleString('el-GR')}
                  icon={Users}
                />
                <StatCard
                  label='Τμήματα'
                  value={overview.periodGrades.classesWithGrades}
                  icon={Layers}
                />
              </CardContent>
            </Card>
          </div>

          {/* ── Tabs ───────────────────────────────────── */}
          <Tabs defaultValue='lesson' className='flex flex-col gap-4'>
            <TabsList className='w-fit bg-muted'>
              <TabsTrigger value='lesson' className='gap-2'>
                <BookOpen className='h-4 w-4' /> Βαθμοί Μαθήματος
              </TabsTrigger>
              <TabsTrigger value='period' className='gap-2'>
                <CalendarClock className='h-4 w-4' /> Βαθμοί Περιόδου
              </TabsTrigger>
            </TabsList>

            <TabsContent value='lesson' className='space-y-4'>
              <GradesByClassTable
                title='Βαθμοί Μαθήματος ανά Τμήμα'
                data={lessonByClass}
                isLoading={lessonClassLoading}
              />

              <Card>
                <CardHeader className='pb-2'>
                  <CardTitle className='text-sm font-medium'>Βαθμοί Μαθήματος ανά Μήνα</CardTitle>
                </CardHeader>
                <CardContent>
                  {lessonMonthLoading ? (
                    <Skeleton className='h-[250px] w-full rounded-lg' />
                  ) : (lessonByMonth?.length ?? 0) === 0 ? (
                    <p className='py-8 text-center text-sm text-muted-foreground'>
                      Δεν υπάρχουν δεδομένα
                    </p>
                  ) : (
                    <ChartContainer config={lessonMonthChartConfig} className='h-[250px] w-full'>
                      <ComposedChart
                        data={lessonByMonth}
                        margin={{ left: 12, right: 12, top: 8, bottom: 4 }}
                      >
                        <CartesianGrid vertical={false} strokeDasharray='3 3' />
                        <XAxis
                          dataKey='month'
                          tickLine={false}
                          axisLine={false}
                          tickMargin={8}
                          tickFormatter={formatMonth}
                        />
                        <YAxis yAxisId='left' tickLine={false} axisLine={false} tickMargin={8} />
                        <YAxis
                          yAxisId='right'
                          orientation='right'
                          tickLine={false}
                          axisLine={false}
                          tickMargin={8}
                          domain={[0, 10]}
                        />
                        <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                        <Bar
                          yAxisId='left'
                          dataKey='totalGrades'
                          fill='var(--color-totalGrades)'
                          radius={[4, 4, 0, 0]}
                        />
                        <Line
                          yAxisId='right'
                          dataKey='averageGrade'
                          type='monotone'
                          stroke='var(--color-averageGrade)'
                          strokeWidth={2.5}
                          dot={{ r: 3, fill: 'var(--color-averageGrade)' }}
                          activeDot={{ r: 5 }}
                        />
                      </ComposedChart>
                    </ChartContainer>
                  )}
                </CardContent>
              </Card>

              <CategoryAccordion
                title='Βαθμοί Μαθήματος ανά Κατηγορία'
                data={lessonByCategory}
                isLoading={lessonCategoryLoading}
              />
            </TabsContent>

            <TabsContent value='period' className='space-y-4'>
              <GradingPeriodsTable data={periodByPeriod} isLoading={periodByPeriodLoading} />

              <GradesByClassTable
                title='Βαθμοί Περιόδου ανά Τμήμα'
                data={periodByClass}
                isLoading={periodClassLoading}
              />

              <CategoryAccordion
                title='Βαθμοί Περιόδου ανά Κατηγορία'
                data={periodByCategory}
                isLoading={periodCategoryLoading}
              />
            </TabsContent>
          </Tabs>
        </>
      ) : null}
    </div>
  );
}

export default GradesTab;
